import { useMemo } from 'react';
import { find as linkifyFind } from 'linkifyjs';
import { toast } from 'react-toastify';
import { PopoverPlacement } from '@idui/react-popover';
import { useOrg, useThunkDispatch } from '../../../utils/reduxHook';
import { InputProps } from '../Input';
import { shortenUrlViaGlooApi } from '../../../actions/requests';
import { InputI18nProps } from '../InputI18n';
import ButtonWithHelp from '../../atoms/ButtonWithHelp';

function ShortenUrlButton({
  value,
  onShortenUrl,
  mediaInputRef,
}: {
  value: InputProps['value'];
  onShortenUrl: InputI18nProps['onShortenUrl'];
  mediaInputRef?: React.MutableRefObject<HTMLDivElement>;
}) {
  const dispatch = useThunkDispatch();
  const org = useOrg();
  const hasLink = useMemo(() => {
    return linkifyFind((value as string) || '', 'url').length > 0;
  }, [value, org]);

  const handleShorten = async () => {
    try {
      const currentStrValue = (value as string) || '';
      const allUrls = linkifyFind(currentStrValue, 'url');
      const longUrls = allUrls.filter(
        (u) => !u.href.includes('bit.ly/') && !u.href.includes(process.env.REACT_APP_SHORTENED_URL_BASE),
      );

      if (allUrls.length === 0) {
        toast.info(`Nothing to shorten`, { autoClose: 2000 });
        return;
      }

      if (longUrls.length === 0) {
        toast.info('All your URLs are already shortened!');
        return;
      }

      const linkifyObj = longUrls[0];
      const results = await dispatch(shortenUrlViaGlooApi(linkifyObj.href));
      const shortUrl = results.data.shortenedUrl;
      const newStr = currentStrValue.replace(linkifyObj.value, shortUrl);
      toast.success(`URL shortened from ${linkifyObj.href} to ${shortUrl}`);
      onShortenUrl(newStr);
      if (mediaInputRef) {
        mediaInputRef.current.innerText = newStr;
      }
    } catch (error) {
      console.error('ERROR SHORTENING', error);
    }
  };

  return (
    <ButtonWithHelp
      helpOptions={{ inline: true, flex: true, fitMaxWidthToBounds: true, mobilePlacement: PopoverPlacement.right }}
      type="transparent"
      helpText="Shorten URL"
      icon="link"
      iconClassName="text-glooMedGray hover:text-skyBlue-600"
      iconSize={20}
      disabled={!hasLink}
      onClick={handleShorten}
      dataDurableId="shortenURLButton"
    />
  );
}

export default ShortenUrlButton;
